import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useDispatch, useSelector } from 'react-redux';
import CustomTileLayer from '../layout/map/CustomTileLayer.js';
import ConteneurCanvasLayer from './ConteneurCanvasLayer';
import ConteneurMarkerPopup from '../map/ConteneurMarkerPopup.js';
import { setSelected } from '../../../modules/conteneurMap/conteneurMap.slice';
import ChecklistKpiPanel from './ChecklistKpiPanel';

/**
 * Icône Leaflet "invisible" : sert uniquement de support pour ouvrir une
 * `Popup` react-leaflet à la position du conteneur sélectionné, sans dessiner
 * un second marqueur par-dessus le carré arrondi du Canvas layer.
 */
const INVISIBLE_ICON = L.divIcon({
    className: 'conteneur-popup-anchor',
    html: '',
    iconSize: [1, 1],
    iconAnchor: [0, 0],
});

/**
 * Marker invisible + popup react-leaflet ouverte automatiquement, branchée sur
 * le `selectedId` du store. Reproduit l'infobulle riche `ConteneurMarkerPopup`
 * de la carte v1 (Patrimoine v1) sur la nouvelle carte Canvas.
 */
function SelectedConteneurPopup({ features, checklistMode = false }) {
    const dispatch = useDispatch();
    const selectedId = useSelector((s) => s.conteneurMap.selectedId);
    const markerRef = useRef(null);

    const selectedConteneur = useMemo(() => {
        if (selectedId == null) return null;
        const f = (features || []).find((x) => x?.properties?.id === selectedId);
        if (!f) return null;
        const props = f.properties || {};
        const coords = f.geometry?.coordinates || [];
        const lng = props.longitude ?? coords[0];
        const lat = props.latitude ?? coords[1];
        const fLat = typeof lat === 'number' ? lat : parseFloat(lat);
        const fLng = typeof lng === 'number' ? lng : parseFloat(lng);
        if (Number.isNaN(fLat) || Number.isNaN(fLng)) return null;
        return { ...props, latitude: fLat, longitude: fLng };
    }, [selectedId, features]);

    // Ouvre la popup automatiquement à chaque changement de sélection
    useEffect(() => {
        if (selectedConteneur && markerRef.current) {
            markerRef.current.openPopup();
        }
    }, [selectedConteneur]);

    if (!selectedConteneur) return null;

    return (
        <Marker
            ref={markerRef}
            position={[selectedConteneur.latitude, selectedConteneur.longitude]}
            icon={INVISIBLE_ICON}
            interactive={false}
            keyboard={false}
            eventHandlers={{
                popupclose: () => dispatch(setSelected(null)),
            }}
        >
            <ConteneurMarkerPopup
                conteneur={selectedConteneur}
                checklistMode={checklistMode}
                onClose={() => dispatch(setSelected(null))}
                onEdit={() => {}}
            />
        </Marker>
    );
}

/**
 * Recalcule la taille de la carte Leaflet quand son conteneur change de
 * dimensions (ex. masquage/affichage de la liste, redimensionnement fenêtre).
 * Sans ça, Leaflet garde la taille initiale et la tuile reste "tronquée".
 */
function MapResizeHandler() {
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        let frame = null;
        const ro = new ResizeObserver(() => {
            // rAF pour éviter le warning "ResizeObserver loop" et laisser le
            // layout se stabiliser avant de mesurer.
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => map.invalidateSize());
        });
        ro.observe(container);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            ro.disconnect();
        };
    }, [map]);
    return null;
}

/**
 * Carte Leaflet pour la vue Patrimoine v2.
 * Le rendering des markers est délégué à `ConteneurCanvasLayer` : un layer
 * `L.Layer.extend` custom qui dessine un picto borne biodéchets en Canvas 2D
 * et gère lui-même le hit-testing au clic (rayon 8px).
 *
 * L'infobulle riche (reprise de la v1) est affichée via un `Marker` invisible
 * ancré au conteneur sélectionné dans le store.
 */
function ConteneurMap({ features, activeIds, checklistMode = false, heatmap = false, ghostInactive = true, kpiRows = null, presentation = false }) {
    return (
        <div style={{ position: 'relative', height: '100%', width: '100%', minHeight: 400 }}>
            <MapContainer
                center={[46.603354, 1.888334]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                preferCanvas
            >
                <CustomTileLayer maxZoom={19} />
                <MapResizeHandler />
                <ConteneurCanvasLayer features={features} activeIds={activeIds} checklistMode={checklistMode} heatmap={heatmap} ghostInactive={ghostInactive} />
                <SelectedConteneurPopup features={features} checklistMode={checklistMode} />
            </MapContainer>
            {checklistMode && kpiRows && <ChecklistKpiPanel rows={kpiRows} presentation={presentation} />}
        </div>
    );
}

export default ConteneurMap;
